import React from 'react';
import { cloneElement } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { matchesPathSegments } from './navMatch';

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  href: string;
  isCollapsed: boolean;
  hasDropdown?: boolean;
  dropdownHeaderLink?: boolean;
  dropdownItems?: {label: string; href: string}[];
  /** Small marker chip after the label (e.g. "V2 preview"). Non-dropdown items only. */
  badge?: string;
  /**
   * Opt-in: match whole path segments and ignore any query string, so
   * `/admin/catalog` is not active on `/admin/catalog-rejections`. Used by the
   * admin sidebar; the brand sidebar keeps the legacy prefix match.
   */
  matchSegments?: boolean;
}

const NavItem: React.FC<NavItemProps> = ({ icon, label, href, isCollapsed, hasDropdown = false, dropdownHeaderLink = false, dropdownItems = [], badge, matchSegments = false }) => {
  const location = useLocation();
  const [isExpanded, setIsExpanded] = React.useState(false);

  // Check if current path starts with the base path for grouped pages
  // Special handling for overview page which can be both "/" and "/overview"
  const isActive = matchSegments
    ? matchesPathSegments(location.pathname, href)
    : href === '/overview'
      ? (location.pathname === '/' || location.pathname.startsWith('/overview'))
      : location.pathname.startsWith(href);

  // Check if any dropdown item is active
  const isDropdownActive = hasDropdown && dropdownItems.some(item =>
    matchSegments ? matchesPathSegments(location.pathname, item.href) : location.pathname.startsWith(item.href)
  );

  const handleClick = () => {
    if (hasDropdown) {
      setIsExpanded(!isExpanded);
    }
  };

  const isTopLevelActive = isActive || isDropdownActive;
  const activeTileClasses = isTopLevelActive ? 'bg-white/10' : 'hover:bg-white/5';
  const labelClasses = isCollapsed
    ? 'ml-0 w-0 overflow-hidden opacity-0'
    : 'ml-3 opacity-100';

  return (
    <div className="relative">
      {hasDropdown ? (
        <>
          {/* Parent Item with Chevron */}
          <div
            onClick={dropdownHeaderLink ? undefined : handleClick}
            title={isCollapsed ? label : undefined}
            className={`flex items-center py-2.5 my-0.5 text-xs font-normal transition-all duration-300 ease-out cursor-pointer rounded-lg ${
              isCollapsed ? 'justify-center px-2' : 'justify-between px-4'
            } ${activeTileClasses} ${
              isTopLevelActive
                ? 'text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {dropdownHeaderLink ? (
              <Link
                to={href}
                onClick={(event) => event.stopPropagation()}
                className={`flex items-center ${isCollapsed ? '' : 'flex-1'}`}
              >
                <span className={`flex items-center justify-center transition-all duration-300 ease-out ${isTopLevelActive ? 'text-white' : 'text-gray-400'} w-5 flex-shrink-0`}>
                  {cloneElement(icon as React.ReactElement, { size: 16 })}
                </span>
                <span className={`transition-all duration-300 ease-out whitespace-nowrap ${labelClasses}`}>
                  {label}
                </span>
              </Link>
            ) : (
              <div className={`flex items-center ${isCollapsed ? '' : 'flex-1'}`}>
                <span className={`flex items-center justify-center transition-all duration-300 ease-out ${isTopLevelActive ? 'text-white' : 'text-gray-400'} w-5 flex-shrink-0`}>
                  {cloneElement(icon as React.ReactElement, { size: 16 })}
                </span>
                <span className={`transition-all duration-300 ease-out whitespace-nowrap ${labelClasses}`}>
                  {label}
                </span>
              </div>
            )}
            {/* Chevron Icon */}
            {!isCollapsed && (
              <button
                type="button"
                onClick={handleClick}
                aria-label={`Toggle ${label} menu`}
                className="ml-2 flex-shrink-0"
              >
                {isExpanded ? (
                  <ChevronUp size={14} className="text-gray-400" />
                ) : (
                  <ChevronDown size={14} className="text-gray-400" />
                )}
              </button>
            )}
          </div>

          {/* Dropdown Items (Accordion) */}
          {isExpanded && !isCollapsed && (
            <div className="overflow-hidden transition-all duration-300 ease-out">
              {dropdownItems.map((item, index) => {
                // Parse the href to handle query params properly
                const [itemPath, itemQuery] = item.href.split('?');
                const currentFullPath = location.pathname + location.search;

                // Determine active state:
                // - Exact match for paths without query (e.g., /portal)
                // - Path prefix + query match for paths with query params (e.g., /portal/total?tab=sales)
                // - Path prefix for other paths (e.g., /portal/categories)
                let isItemActive = false;
                if (matchSegments) {
                  // Query-string children (tabs) must match their own query too.
                  isItemActive = matchesPathSegments(location.pathname, itemPath)
                    && [...new URLSearchParams(itemQuery ?? '')].every(
                      ([key, value]) => new URLSearchParams(location.search).get(key) === value,
                    );
                } else if (itemQuery) {
                  // Has query params: match path prefix and check if current URL starts with full href
                  isItemActive = location.pathname.startsWith(itemPath) && currentFullPath.startsWith(item.href.split('?')[0]);
                } else if (itemPath === '/portal') {
                  // Style View: exact match only (not when on /portal/total or /portal/categories)
                  isItemActive = location.pathname === '/portal';
                } else {
                  // Regular path: prefix match
                  isItemActive = location.pathname.startsWith(itemPath);
                }

                return (
                  <Link
                    key={index}
                    to={item.href}
                    className={`flex items-center py-2 pl-12 pr-4 text-xs transition-all duration-200 ${
                      isItemActive
                        ? 'text-white bg-white/10'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <Link
          to={href}
          title={isCollapsed ? label : undefined}
          className={`flex items-center py-2.5 my-0.5 text-xs font-normal transition-all duration-300 ease-out relative rounded-lg ${
            isCollapsed ? 'justify-center px-2' : 'px-4'
          } ${activeTileClasses} ${
            isTopLevelActive
              ? 'text-white'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <span className={`flex items-center justify-center transition-all duration-300 ease-out ${isTopLevelActive ? 'text-white' : 'text-gray-400'} w-5 flex-shrink-0`}>
            {cloneElement(icon as React.ReactElement, { size: 16 })}
          </span>
          <span className={`transition-all duration-300 ease-out whitespace-nowrap ${labelClasses}`}>
            {label}
          </span>
          {badge && !isCollapsed && (
            <span className="ml-2 flex-shrink-0 rounded-full border border-brand-navy/20 bg-brand-navy/5 px-1.5 py-0.5 text-[9px] font-semibold text-brand-navy whitespace-nowrap">
              {badge}
            </span>
          )}
        </Link>
      )}
    </div>
  );
};

export default NavItem;
